import { InputNumber as InputNumberAntd, Space } from 'antd';
import { cn } from 'cn';
import {
    type FocusEvent,
    type KeyboardEvent,
    type KeyboardEventHandler,
    type ReactNode,
    useRef,
    useState,
} from 'react';
import type { FormItemProps } from '@/components/types';
import { numberToString, stringToNumber } from '@/std/format';
import { truncateDecimal } from '@/std/math';
import { allowedKeys } from './allowedKeys';

export function InputNumber({
    allowNegative = false,
    autoFocus = false,
    addonAfter,
    className,
    decimalScale = 0,
    disabled,
    id,
    max,
    min,
    placeholder,
    readOnly = false,
    ref,
    selectOnFocus = false,
    size,
    type,
    value,
    style,
    onBlur,
    onChange,
    onKeyDown,
    onPressEnter,
}: FormItemProps<number | null, HTMLInputElement | null> & {
    /** Permitir números negativos. Default: false */
    allowNegative?: boolean;
    /** Foco automático. Default: false */
    autoFocus?: boolean;
    addonAfter?: ReactNode;
    className?: string;
    /** Quantidade de casas decimais após a virgula. Default: 0 */
    decimalScale?: number;
    disabled?: boolean;
    max?: number;
    min?: number;
    placeholder?: string;
    readOnly?: boolean;
    selectOnFocus?: boolean;
    size?: 'large' | 'middle' | 'small';
    type?: '$' | '%';
    style?: React.CSSProperties;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onPressEnter?: KeyboardEventHandler<HTMLInputElement>;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const innerRef = selectOnFocus ? inputRef : ref;

    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (): void => {
        setIsFocused(true);

        if (selectOnFocus) {
            // Precisa do timeout, para postegar o evento, e deixar o PARSE/FORMAT ir primeiro antes de selecionar.
            setTimeout(() => {
                if (
                    innerRef &&
                    'current' in innerRef &&
                    innerRef.current instanceof HTMLInputElement
                ) {
                    innerRef.current.select();
                }
            }, 20);
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(e);
        }
    };

    const fixValue = (newValue: number | null | undefined): number | null => {
        if (typeof newValue !== 'number') {
            return null;
        }

        // Se não permite valor negativo e o valor é menor que 0 então retorna o valor positivo
        if (!allowNegative && newValue < 0) {
            return Math.abs(newValue);
        }

        // Se não pode casas decimais trunca o valor
        if (decimalScale === 0) {
            return Math.trunc(newValue);
        }

        // Força o número a ter as casas decimais informadas
        return truncateDecimal(newValue, decimalScale);
    };

    const handleOnChange = (newValue: number | null): void => {
        if (onChange) {
            onChange(fixValue(newValue));
        }
    };

    const placeholderInner = placeholder
        ? placeholder
        : decimalScale === 0
          ? '0'
          : numberToString(0, 'decimal', decimalScale);

    const innerValue = fixValue(value);

    const formatter = (value: string | undefined): string => {
        if (!value) {
            return '';
        }

        // Se o input está com foco então retorna o valor sem formatar
        if (isFocused) {
            return value.replace('.', ',');
        }

        const num = Number(value);

        if (Number.isNaN(num)) {
            return '';
        }

        return numberToString(num, 'decimal', decimalScale);
    };

    const parser = (displayValue: string | undefined): number | null => {
        if (typeof displayValue !== 'string' || displayValue === '') {
            return null;
        }

        if (displayValue === '-') {
            // @ts-expect-error Para lidar com números negativos
            return displayValue;
        }

        return stringToNumber(displayValue);
    };

    const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (onKeyDown) {
            onKeyDown(e);
        }

        // Se não deixa negativo não deixa digitar -
        if (!allowNegative && e.key === '-') {
            e.preventDefault();
        }

        // Se for apenas números inteiros não permite . ou ,
        if (decimalScale === 0 && (e.key === '.' || e.key === ',')) {
            e.preventDefault();
        }

        // Verifica se a tecla pressionada é valida
        if (!allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const input = (
        <InputNumberAntd<number>
            autoFocus={autoFocus}
            className={cn('w-full', className)}
            controls={decimalScale === 0}
            decimalSeparator=','
            disabled={disabled}
            //@ts-expect-error - formatter retorna uma string não um number
            formatter={formatter}
            id={id}
            inputMode={decimalScale === 0 ? 'numeric' : 'decimal'}
            max={max}
            min={min}
            onBlur={handleBlur}
            onChange={handleOnChange}
            onFocus={handleFocus}
            onKeyDown={handleOnKeyDown}
            onPressEnter={onPressEnter}
            //@ts-expect-error - parser não aceita null
            parser={parser}
            placeholder={placeholderInner}
            readOnly={readOnly}
            // @ts-expect-error - ref está com tipo errado na lib do antd
            ref={innerRef}
            size={size}
            style={style}
            value={innerValue}
        />
    );

    if (type || addonAfter) {
        return (
            <Space.Compact className='w-full'>
                {type === '$' && <Space.Addon>R$</Space.Addon>}
                {input}
                {type === '%' && <Space.Addon>%</Space.Addon>}
                {addonAfter}
            </Space.Compact>
        );
    }

    return input;
}
